import { ethers } from "ethers";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Loader from "react-spinners/GridLoader";
import styled from "styled-components";

import ProvenanceRecordTable from "../../components/ProvenanceRecordTable";
import { useGnarlyGoatsContracts } from "../../hooks/useGnarlyGoatsContracts";
import { Metadata, useProvenance } from "../../hooks/useProvenance";
import {
  DashboardWrapper,
  ExternalLink,
  Interface,
  PageWrapper,
  StyledLink,
  Text,
  Title
} from "../../theme";

const StyledTable = styled.table`
	font-size: 12pt;
`

const ConcatenatedImageHashes = styled.div`
	display: inline-block;
	word-break: break-all;
	overflow-wrap: anywhere;
	text-align: justify;
	overflow-y: scroll;
	overflow-x: hidden;
	max-height: 150px;
	box-sizing: border-box;
	inline-size: 85%;
`

const ProvenancePage = (props: any) => {
	const { contract } = useGnarlyGoatsContracts()
	const { loading, concatImageHashes, provenanceHash, startingIndex } =
		useProvenance()

	return (
		<PageWrapper>
			<DashboardWrapper noTheme>
				<Title>Gnarly Goats Island Club Provenance Record</Title>

				<Text owner1>
					DISCLAIMER: The information presented below does not represent the
					provenance record of the final Gnarly Goats artwork collection and
					should only be considered as a placeholder for testing purposes.
				</Text>
			</DashboardWrapper>

			<DashboardWrapper>
				<Title>Key Details</Title>

				<Loader
					color="#2172E5"
					loading={loading}
					css={`
						padding: 2rem 0;
					`}
				/>

				{!loading && (
					<>
						<br></br>
						<Interface justifyContent="flex-start">
							<Text raw color2>
								{`Contract Address: `}
							</Text>
							<StyledLink
								href={`https://rinkeby.etherscan.io/address/${
									contract?.address ?? ''
								}`}
								target="_blank">
								{contract ? contract.address : 'Loading...'}
							</StyledLink>
						</Interface>

						<Interface justifyContent="flex-start">
							<Text raw color2>
								{`Starting Index: `}
							</Text>
							<Text indent color1>
								{startingIndex}
							</Text>
						</Interface>

						<Interface justifyContent="flex-start">
							<Text raw color2>
								{`Provenance Hash: `}
							</Text>
							<Text raw color1>
								{provenanceHash}
							</Text>
						</Interface>

						<Interface justifyContent="flex-start">
							<Text raw color2>
								Concatenated Image Hashes:
							</Text>
						</Interface>

						<ConcatenatedImageHashes>
							{concatImageHashes}
						</ConcatenatedImageHashes>
					</>
				)}
			</DashboardWrapper>

			<DashboardWrapper noTheme>
				<Title primary1>How is the Starting Index Calculated?</Title>
				<Text color1 left>
					In order to ensure a fair token release schedule for all users, the
					Gnarly Goats smart contract initializes its starting index using a
					random number generated on-chain. Specifically, the contract
					integrates with Chainlink's VRF (Verifiable Random Function) to
					calculate a provably-fair and verifiable random starting index for the
					collection.
					<br />
					<br />
					Learn more about how smart contracts can integrate with Chainlink and
					it's VRF functionality{' '}
					<ExternalLink href="https://docs.chain.link/docs/chainlink-vrf/">
						here
					</ExternalLink>
					.
				</Text>

				<Title primary1>How is the Provenance Hash Calculated?</Title>
				<Text color1 left>
					The final provenance hash is calculated by taking the SHA-256 hash of
					the concatenated image hash string. The concatenated image hash string
					is simply the result of joining each image's individual SHA-256 hash
					with respect to the initial sequence index. This information can be
					found in the table of unique token records below.
				</Text>
			</DashboardWrapper>

			<DashboardWrapper>
				<Title>Unique Token Records</Title>

				<Text color1>
					The table below contains the initial sequence index, token ID, SHA-256
					hash, and IPFS CID of every Gnarly Goat.
				</Text>

				<Text>
					Each Gnarly Goat token is assigned an ID according to the following
					formula:
				</Text>

				<Text primary2>
					{'(Initial Sequence Index + Starting Index) % 10,000 → Token ID'}
				</Text>

				<Text primary1>
					{'INIT SEQ INDEX  |  TOKEN ID   |  SHA-256 HASH  |  IPFS CID'}
				</Text>

				<ProvenanceRecordTable />
			</DashboardWrapper>
		</PageWrapper>
	)
}
export default ProvenancePage
